import type { Id, TicketListItem } from '../../api/types';

/**
 * CREATE AND EDIT A TICKET — PH-57's draft logic.
 *
 * 🔴 THE TICKET: *"new tickets on PH not have create new tickets in UI planhub by human"*. The API
 * has had `POST /tickets` and `PATCH /tickets/:id` since the beginning; the web app called NEITHER.
 * The only ticket write it made was `/tickets/:id/rank`. So a person could reorder the backlog and
 * move a ticket's status but could not create one, or change its title, points, assignee or sprint.
 *
 * ⭐ WHY THE DRAFT IS A SEPARATE MODULE FROM THE DIALOG, exactly as `sprint-form.ts` is: every rule
 * worth getting right here — what a number means, what an empty box means, what is sent and what is
 * withheld — is decidable without a DOM. The dialog renders; this file decides.
 *
 * ⛔ STATUS IS ABSENT FROM THIS FILE ON PURPOSE. `updateTicketBody` refuses `status` outright — not
 * by stripping it, but with a 400 that fails the WHOLE request. Status moves only through
 * `POST /tickets/:id/transition`, so that the history can never be bypassed. A `status` key added to
 * this draft would not be a feature that half-works; it would make every save 400.
 */

export interface TicketDraft {
  title: string;
  description: string;
  type: string;
  priority: string;
  impact: string;
  /** ⚠️ A STRING, because it is what a person typed. See `pointsProblem`. */
  storyPoints: string;
  assignee: string;
  sprintId: string;
  versionId: string;
  startDate: string;
  endDate: string;
  /** Comma-separated as typed; split and trimmed on the way out. */
  modules: string;
}

/**
 * The empty-selection sentinel for every `<select>`.
 *
 * ⭐ `''` rather than `null`, because a DOM `<option value={null}>` renders the STRING `"null"` and
 * would be sent as a version whose id is the four characters n-u-l-l. The sentinel is a string
 * because the DOM only has strings; `null` appears at the API boundary and nowhere before it.
 */
export const NONE = '';

export const TITLE_MAX = 500;
export const DESCRIPTION_MAX = 20_000;
export const ASSIGNEE_MAX = 255;
export const MODULE_MAX = 120;
export const MODULES_MAX = 20;
export const POINTS_MAX = 999;

/**
 * ⭐ OR'S CONVENTION, STATED ON THE FORM RATHER THAN LEFT IN A MEMORY FILE: 1 point = 8 hours = one
 * AI working day = one man-day, and it measures DURATION, not difficulty. It is written here so the
 * dialog and any future estimate helper quote the same sentence.
 */
export const POINTS_HINT = '1 point = 8 hours = one working day. Duration, not difficulty.';

export function blankDraft(defaults: { sprintId?: Id | null; type?: string } = {}): TicketDraft {
  return {
    title: '',
    description: '',
    type: defaults.type ?? '',
    priority: '',
    impact: NONE,
    storyPoints: '',
    assignee: '',
    sprintId: defaults.sprintId == null ? NONE : String(defaults.sprintId),
    versionId: NONE,
    startDate: '',
    endDate: '',
    modules: '',
  };
}

export function draftOf(ticket: TicketListItem): TicketDraft {
  return {
    title: ticket.title,
    description: ticket.description ?? '',
    type: ticket.type,
    priority: ticket.priority,
    impact: ticket.impact ?? NONE,
    // ⚠️ The API returns `story_points` as a STRING — `"1.00"`, because it is a numeric column and
    //   pg does not lose precision by guessing. Showing `"1.00"` in the box would make every reopened
    //   form look edited, so it is normalised to the shortest exact form: `1`, `0.5`, `1.5`.
    storyPoints: formatPoints(ticket.story_points),
    assignee: ticket.assignee ?? '',
    sprintId: ticket.sprint_id == null ? NONE : String(ticket.sprint_id),
    versionId: ticket.version_id == null ? NONE : String(ticket.version_id),
    startDate: ticket.start_date ?? '',
    endDate: ticket.end_date ?? '',
    modules: (ticket.modules ?? []).join(', '),
  };
}

/**
 * `"1.00"` → `"1"`, `"0.50"` → `"0.5"`, `null` → `""`.
 *
 * ⛔ Not `parseFloat(...).toString()` alone: a value the API sends that is not a number at all must
 * survive to the box unchanged rather than becoming `NaN` — the person editing can then see and fix
 * whatever is actually stored, which is strictly better than the form silently blanking it.
 */
export function formatPoints(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '';
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return raw;
  return String(parsed);
}

/**
 * Points are the field most likely to be typed wrong, and each refusal names the actual rule.
 *
 * ⭐ DECIMALS ARE LEGAL AND THE TEST SAYS SO. Half a day is a real estimate and the team uses `0.5`
 * and `1.5`. An integer-only rule here would be a UI inventing a constraint the API does not have —
 * `storyPoints` is `z.number().min(0).max(999)`, not `.int()`.
 */
export function pointsProblem(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return `Story points must be a number — '${trimmed}' is not one.`;
  if (parsed < 0) return 'Story points cannot be negative.';
  if (parsed > POINTS_MAX) return `Story points cannot exceed ${POINTS_MAX}.`;
  return null;
}

/**
 * Our own pre-flight refusal, so an invalid draft writes NOTHING.
 *
 * ⛔ THE POINT OF REFUSING HERE rather than letting the API do it: the API's answer to a bad body is
 * `the request body or query was not valid`, which does not tell a person which box to look at. Same
 * reasoning as `draftProblem` in `sprint-form.ts`.
 */
export function draftProblem(draft: TicketDraft): string | null {
  if (draft.title.trim() === '') return 'A ticket needs a title.';
  if (draft.title.trim().length > TITLE_MAX) return `A title is at most ${TITLE_MAX} characters.`;
  if (draft.type === '') return 'A ticket needs a type.';
  if (draft.description.length > DESCRIPTION_MAX) {
    return `A description is at most ${DESCRIPTION_MAX} characters.`;
  }
  if (draft.assignee.trim().length > ASSIGNEE_MAX) {
    return `An assignee is at most ${ASSIGNEE_MAX} characters.`;
  }

  const points = pointsProblem(draft.storyPoints);
  if (points) return points;

  const modules = splitModules(draft.modules);
  if (modules.length > MODULES_MAX) return `At most ${MODULES_MAX} modules.`;
  if (modules.some((module) => module.length > MODULE_MAX)) {
    return `Each module is at most ${MODULE_MAX} characters.`;
  }

  // ⚠️ Checked as STRINGS. Both are `YYYY-MM-DD` from `type="date"`, which compares correctly
  //   lexicographically — and constructing a `Date` here would drag a timezone into a calendar
  //   question that does not have one.
  if (draft.startDate !== '' && draft.endDate !== '' && draft.endDate < draft.startDate) {
    return 'The end date is before the start date.';
  }

  return null;
}

export function splitModules(raw: string): string[] {
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

export interface TicketCreateBody {
  projectId: Id;
  title: string;
  type: string;
  description?: string;
  priority?: string;
  impact?: string;
  storyPoints?: number;
  assignee?: string;
  sprintId?: Id;
  versionId?: Id;
  parentId?: Id;
  startDate?: string;
  endDate?: string;
  modules?: string[];
}

/**
 * The CREATE body.
 *
 * ⛔ OPTIONAL FIELDS ARE OMITTED, NOT SENT AS `null`, matching `createTicketBody`'s `.nullish()` and
 * keeping the request honest about what the person actually filled in.
 * ⛔ AND THE BODY IS `.strict()` SINCE PH-33 — a key this function invents is a 400 naming it, not a
 * silently ignored field. Every key below appears in `createTicketBody`; nothing else may be added
 * here without adding it there first.
 */
export function createBody(
  draft: TicketDraft,
  context: { projectId: Id; parentId?: Id | null },
): TicketCreateBody {
  const description = draft.description.trim();
  const priority = draft.priority.trim();
  const assignee = draft.assignee.trim();
  const points = draft.storyPoints.trim();
  const modules = splitModules(draft.modules);

  return {
    projectId: context.projectId,
    title: draft.title.trim(),
    type: draft.type,
    ...(description === '' ? {} : { description }),
    ...(priority === '' ? {} : { priority }),
    ...(draft.impact === NONE ? {} : { impact: draft.impact }),
    ...(points === '' ? {} : { storyPoints: Number(points) }),
    ...(assignee === '' ? {} : { assignee }),
    ...(draft.sprintId === NONE ? {} : { sprintId: draft.sprintId }),
    ...(draft.versionId === NONE ? {} : { versionId: draft.versionId }),
    ...(context.parentId == null ? {} : { parentId: context.parentId }),
    ...(draft.startDate === '' ? {} : { startDate: draft.startDate }),
    ...(draft.endDate === '' ? {} : { endDate: draft.endDate }),
    ...(modules.length === 0 ? {} : { modules }),
  };
}

export type TicketEditBody = Partial<{
  title: string;
  description: string | null;
  type: string;
  priority: string;
  impact: string | null;
  storyPoints: number | null;
  assignee: string | null;
  sprintId: Id | null;
  versionId: Id | null;
  startDate: string | null;
  endDate: string | null;
  modules: string[];
}>;

/**
 * The EDIT body: ONLY what actually changed.
 *
 * 🔴 A DIFF, NOT THE WHOLE FORM, and it is a correctness choice rather than a saving — the same one
 *   `sprintEditBody` makes and for the same reason. `PATCH /tickets/:id` is partial, so sending
 *   every field means a person who fixed a typo in the title also writes back the assignee, sprint
 *   and points AS THEY WERE WHEN THEIR FORM OPENED, silently undoing whatever anyone else changed in
 *   between. On a board several people are looking at, last-writer-wins over the whole row is a way
 *   to lose work that nobody can even see happening.
 *
 * ⭐ CLEARING IS `null`; KEEPING IS ABSENCE. That is precisely the distinction `updateTicketBody`'s
 *   `.nullable()` (not `.nullish()`) draws. Emptying the assignee box must send `assignee: null`;
 *   not touching it must send no `assignee` key at all. Anything that collapses those two produces a
 *   form that cannot unassign, or one that unassigns by accident.
 *
 * ⚠️ `modules` IS ALL-OR-NOTHING and cannot be cleared to `null` — the API types it as an ARRAY,
 *   never nullable, so "no modules" is `[]`. Emptying the box therefore sends `[]`, and the
 *   comparison below is on the JOINED string so a reorder counts as a change (it is, to the server).
 */
export function editBody(before: TicketListItem, draft: TicketDraft): TicketEditBody {
  const title = draft.title.trim();
  const description = draft.description.trim();
  const priority = draft.priority.trim();
  const assignee = draft.assignee.trim();
  const points = draft.storyPoints.trim();
  const modules = splitModules(draft.modules);

  const impact = draft.impact === NONE ? null : draft.impact;
  const sprintId = draft.sprintId === NONE ? null : draft.sprintId;
  const versionId = draft.versionId === NONE ? null : draft.versionId;
  const startDate = draft.startDate === '' ? null : draft.startDate;
  const endDate = draft.endDate === '' ? null : draft.endDate;
  const nextPoints = points === '' ? null : Number(points);

  // ⭐ Compared through `formatPoints`, not raw. The stored value is `"1.00"` and the box holds `"1"`
  //   — a string comparison would call every untouched form a change and PATCH points on every save.
  const pointsChanged = formatPoints(before.story_points) !== formatPoints(points);

  return {
    ...(title === before.title ? {} : { title }),
    ...(description === (before.description ?? '')
      ? {}
      : { description: description === '' ? null : description }),
    ...(draft.type === before.type ? {} : { type: draft.type }),
    ...(priority === before.priority || priority === '' ? {} : { priority }),
    ...(impact === (before.impact ?? null) ? {} : { impact }),
    ...(pointsChanged ? { storyPoints: nextPoints } : {}),
    ...(assignee === (before.assignee ?? '')
      ? {}
      : { assignee: assignee === '' ? null : assignee }),
    ...(sprintId === (before.sprint_id == null ? null : String(before.sprint_id))
      ? {}
      : { sprintId }),
    ...(versionId === (before.version_id == null ? null : String(before.version_id))
      ? {}
      : { versionId }),
    ...(startDate === (before.start_date ?? null) ? {} : { startDate }),
    ...(endDate === (before.end_date ?? null) ? {} : { endDate }),
    ...(modules.join(' ') === (before.modules ?? []).join(' ') ? {} : { modules }),
  };
}

/**
 * ⚠️ An EMPTY edit is a legitimate result and means nothing changed. The caller decides — see
 * `useTicketEditor`, which closes the form rather than reporting a save that saved nothing.
 */
export function isEmptyEdit(edit: TicketEditBody): boolean {
  return Object.keys(edit).length === 0;
}
